/**
 * Manual mat counts by location (AST-88, AST-89)
 *
 * Mark, 2026-10-07 (AST-87 thread): "From is decremented, To is added", and
 * entries with no From/To are placed "at project level, with the project
 * marker on the map".
 *
 * Each entry has a signed quantity q (negative = removal, ASS-38). Where the
 * mats are:
 *   - neither From nor To  -> project level  +q   (delivered/removed, place unknown)
 *   - To only              -> To             +q   (delivered to a mapped place)
 *   - From only            -> From           -q   (removed from a mapped place)
 *   - From and To          -> From -q, To +q      (moved between mapped places)
 * Decision (AST-88 PR): a From+To entry is a MOVE inside the project -- it
 * relocates mats and does not change the project's total. That's Mark's
 * example (Yard A -12, Str 19 +12) read literally.
 *
 * An entry pointing at an unknown or retired location falls back to project
 * level for that side, so no mats vanish from the totals.
 *
 * Counts are by Mat Type (the subtype id the entry carries). Pure; no I/O.
 */

export const PROJECT_LEVEL = '__project__';

/**
 * How an entry changes the project's manual total, as a multiplier on its
 * signed quantity: +1 (no From: delivered to the project, or to a mapped
 * place), -1 (From only: taken out of the project), 0 (From and To: moved
 * inside it). The single rule both the MAT (manual) row and See Mat
 * Locations use.
 */
export function entryProjectDelta(entry) {
  const from = !!entry?.fromElementId;
  const to = !!entry?.toElementId;
  if (from && to) return 0;
  if (from) return -1;
  return 1;
}

const add = (m, loc, type, q) => {
  if (!m[loc]) m[loc] = {};
  m[loc][type] = (m[loc][type] || 0) + q;
};

/**
 * @param entries   [{ quantity, matTypeId, fromElementId?, toElementId? }]
 * @param elements  active + retired area elements [{ id, retired? }] -- an
 *                  entry pointing at an unknown or retired element is counted
 *                  at project level, so no mats disappear from the totals.
 * @param columnOf  optional (entry) => column key. Defaults to the entry's
 *                  subtype (matTypeId), which is what the map badges and the
 *                  old Mat Locations table use. AST-120 passes one that keys
 *                  by manual asset, so the SAME location and move rules apply
 *                  to either grouping -- only the column changes.
 * @returns { byLocation: { [elementId|PROJECT_LEVEL]: { [columnKey]: n } } }
 */
export function countsByLocation(entries, elements, columnOf = (e) => e.matTypeId) {
  const known = new Set((elements || []).filter((e) => e && !e.retired).map((e) => e.id));
  const loc = (id) => (id && known.has(id) ? id : null);
  const byLocation = {};
  for (const e of entries || []) {
    const q = Number(e?.quantity);
    if (!Number.isFinite(q) || q === 0 || !e.matTypeId) continue;
    const col = columnOf(e);
    // A side that names an unknown/retired location counts at project level.
    const to = e.toElementId ? (loc(e.toElementId) || PROJECT_LEVEL) : null;
    const from = e.fromElementId ? (loc(e.fromElementId) || PROJECT_LEVEL) : null;
    if (!to && !from) {
      add(byLocation, PROJECT_LEVEL, col, q);
      continue;
    }
    if (to) add(byLocation, to, col, q);
    if (from) add(byLocation, from, col, -q);
  }
  for (const [k, byType] of Object.entries(byLocation)) {
    for (const [t, n] of Object.entries(byType)) if (n === 0) delete byType[t];
    if (Object.keys(byType).length === 0) delete byLocation[k];
  }
  return { byLocation };
}

/**
 * The project's net manual total implied by these entries under the rules
 * above: moves (From+To) net to zero, everything else is its signed q.
 */
export function projectTotal(entries, elements) {
  return Object.values(countsByLocation(entries, elements).byLocation).reduce((a, t) => a + totalOf(t), 0);
}

export const totalOf = (byType) => Object.values(byType || {}).reduce((a, b) => a + b, 0);

/**
 * Rows for the See Mat Locations table (AST-88, narrowed by AST-99).
 *
 * AST-99 (Mark): "If the location row has zeros across all mat types then
 * don't show the row" and "if the mat type column has zeros for all
 * locations then don't show the column". This reverses AST-88's "a complete
 * inventory of mapped locations" -- the table is now only what has mats.
 *
 * Hiding never changes the arithmetic: a hidden row/column is zero in every
 * cell, so grandTotal still equals projectTotal().
 *
 * matTypes: [{ id, name }] -- the candidate columns, in order. The surviving
 * ones come back as `columns`, and every row's `cells` is aligned to it, so
 * callers must index cells against `columns` and not their own matTypes.
 */
export function locationTable(entries, elements, matTypes, sections, columnOf) {
  const { byLocation } = countsByLocation(entries, elements, columnOf);
  const active = (elements || []).filter((e) => e && !e.retired);
  const allCols = (matTypes || []).map((t) => t.id);
  const rawCells = (id) => {
    const byType = byLocation[id] || {};
    return allCols.map((c) => byType[c] || 0);
  };

  const rawGroups = (sections || []).map(({ key, label }) => ({
    key,
    label,
    rows: active
      .filter((e) => e.section === key)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
      .map((e) => ({ id: e.id, name: e.name, cells: rawCells(e.id) })),
  }));
  const rawProject = { id: PROJECT_LEVEL, name: 'Project level (no location)', cells: rawCells(PROJECT_LEVEL) };
  const everyRow = [...rawGroups.flatMap((g) => g.rows), rawProject];

  // A column survives if any location (or project level) has mats of it.
  const keep = allCols.map((_, i) => everyRow.some((r) => r.cells[i] !== 0));
  const columns = (matTypes || []).filter((_, i) => keep[i]);
  const narrow = (r) => {
    const cells = r.cells.filter((_, i) => keep[i]);
    return { id: r.id, name: r.name, cells, total: cells.reduce((a, b) => a + b, 0) };
  };
  // A row survives if it has mats. Hidden columns are zero everywhere, so
  // testing the narrowed cells is the same as testing all of them.
  const hasMats = (r) => r.cells.some((n) => n !== 0);

  const groups = rawGroups
    .map((g) => ({ key: g.key, label: g.label, rows: g.rows.filter(hasMats).map(narrow) }))
    .filter((g) => g.rows.length > 0);
  // Project level still shows when it has mats, and is the whole table when
  // nothing is mapped (AST-88) -- so a project with entries but no KMZ areas
  // never renders an empty dialog.
  const project = hasMats(rawProject) || groups.length === 0 ? narrow(rawProject) : null;

  const shown = [...groups.flatMap((g) => g.rows), ...(project ? [project] : [])];
  const grand = columns.map((_, i) => shown.reduce((a, r) => a + r.cells[i], 0));
  return {
    columns,
    groups,
    project,
    grandTotal: { cells: grand, total: grand.reduce((a, b) => a + b, 0) },
  };
}

/**
 * Badges for map pills (AST-89, extended by AST-111).
 *
 * One per structure whose manual count is non-zero, plus -- when it has any --
 * one for the project-level count (entries with no From/To).
 *
 * AST-111 (Mark): negatives get an amber pill instead of being dropped.
 * Before this, `.filter((b) => b.total > 0)` meant a structure mats had
 * *left* (e.g. -25) drew nothing at all, so on the map it looked identical to
 * a structure that never had any -- while See Mat Locations showed the -25.
 * Zero still draws nothing (AST-99: Mark chose to hide empty rows/pills).
 *
 * `negative` rides on each badge so the component picks green vs amber
 * without re-deriving the sign. Shape:
 * [{ id, name, position: [lng, lat], total, negative, projectLevel, byType }]
 *
 * projectPoint: the project's own { lat, lng }. Mark asked for the
 * project-level pill "adjacent to project marker not boundary", and that
 * point is what the hexagon P/Y marker already uses. Pass nothing (or a
 * project with no point) and there is simply no project-level pill -- the
 * count still shows in See Mat Locations and the inventory panel.
 *
 * manualAssets (AST-125 MP): optional [{ id, name, subtypeId, archived }].
 * When given, each pill's breakdown is one row per MANUAL ASSET (L16 · 25)
 * instead of per subtype, sorted like the Inventory scoreboard (MP-2:
 * by subtype in catalog order, then name A->Z, a subtype's unassigned row
 * first). Counts with no manual asset stay under their subtype name (MP-4).
 * Totals are unchanged either way -- only the grouping of the rows differs.
 */
export function structureBadges(entries, elements, matTypes, projectPoint, manualAssets) {
  const assets = manualAssets && manualAssets.length ? manualAssets : null;
  const assetById = assets ? Object.fromEntries(assets.map((a) => [a.id, a])) : null;
  // Key each count by manual asset when we know it, else by subtype.
  const columnOf = assets
    ? (e) => (e.manualAssetId && assetById[e.manualAssetId] ? `a:${e.manualAssetId}` : `s:${e.matTypeId}`)
    : undefined;
  const { byLocation } = countsByLocation(entries, elements, columnOf);
  const nameOf = Object.fromEntries((matTypes || []).map((t) => [t.id, t.name]));
  const subtypeOrder = new Map((matTypes || []).map((t, i) => [t.id, i]));
  const natural = (x, y) => x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' });

  const breakdown = (byKey) => {
    const rows = Object.entries(byKey).filter(([, n]) => n !== 0).map(([key, n]) => {
      if (!assets) return { id: key, name: nameOf[key] || 'Unknown type', n };
      if (key.startsWith('a:')) {
        const a = assetById[key.slice(2)];
        return { id: key, name: a.name, n, subtypeId: a.subtypeId, kind: 'asset' };
      }
      const sid = key.slice(2);
      return { id: key, name: nameOf[sid] || 'Unknown type', n, subtypeId: sid, kind: 'subtype' };
    });
    if (!assets) return rows;
    // MP-2: the scoreboard's order (utils/scoreboardRows).
    const ord = (r) => (subtypeOrder.has(r.subtypeId) ? subtypeOrder.get(r.subtypeId) : Number.MAX_SAFE_INTEGER);
    return rows
      .sort((x, y) => ord(x) - ord(y) || (x.kind === y.kind ? natural(x.name, y.name) : (x.kind === 'subtype' ? -1 : 1)))
      .map(({ id, name, n }) => ({ id, name, n }));
  };

  const badges = (elements || [])
    .filter((e) => e && !e.retired && e.section === 'structures'
      && Number.isFinite(e.lat) && Number.isFinite(e.lng))
    .map((e) => {
      const byType = byLocation[e.id] || {};
      const total = totalOf(byType);
      return {
        id: e.id,
        name: e.name,
        position: [e.lng, e.lat],
        total,
        negative: total < 0,
        projectLevel: false,
        byType: breakdown(byType),
      };
    })
    .filter((b) => b.total !== 0);

  // AST-111: the project-level pill, beside the project's own marker.
  const lat = projectPoint?.lat;
  const lng = projectPoint?.lng;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    const byType = byLocation[PROJECT_LEVEL] || {};
    const total = totalOf(byType);
    if (total !== 0) {
      badges.push({
        id: PROJECT_LEVEL,
        name: 'Project level',
        position: [lng, lat],
        total,
        negative: total < 0,
        projectLevel: true,
        byType: breakdown(byType),
      });
    }
  }

  return badges;
}
